// UsersPanel.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api';
import { STANDALONE_DEPLOYMENT_EDITION, getDeploymentEdition } from '../../deploymentEdition';

const isStandaloneDeployment = getDeploymentEdition() === STANDALONE_DEPLOYMENT_EDITION;
const DEFAULT_PAGE_SIZE = 10;
const API_PAGE_SIZE = 100;

const sortOptions = [
  { value: 'createdAt', label: 'Signed up' },
  { value: 'email', label: 'Email' },
  ...(!isStandaloneDeployment ? [{ value: 'generationCredits', label: 'Credits' }] : []),
  { value: 'isPremiumUser', label: 'Premium' },
  { value: 'isEmailVerified', label: 'Verified' },
];

const userFilters = [
  {
    id: 'all',
    label: 'Total users',
    emptyMessage: 'No users found.',
    predicate: () => true,
  },
  {
    id: 'verified',
    label: 'Verified',
    emptyMessage: 'No verified users found.',
    predicate: (user) => user.isEmailVerified,
  },
  {
    id: 'premium',
    label: 'Premium',
    emptyMessage: 'No premium users found.',
    predicate: (user) => user.isPremiumUser,
  },
];

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString();
}

function searchableValue(user) {
  return [
    user.email,
    user.displayName,
    user.username,
    user._id,
  ].filter(Boolean).join(' ').toLowerCase();
}

function getSortValue(user, sortBy) {
  if (sortBy === 'createdAt') {
    return user.createdAt ? new Date(user.createdAt).getTime() : 0;
  }

  if (sortBy === 'generationCredits') {
    return Number(user.generationCredits || 0);
  }

  if (sortBy === 'isPremiumUser' || sortBy === 'isEmailVerified') {
    return user[sortBy] ? 1 : 0;
  }

  return String(user[sortBy] || '').toLowerCase();
}

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex min-w-[48px] justify-center rounded px-2.5 py-1 text-sm font-medium ring-1 ring-inset ${
        active
          ? 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/20'
          : 'bg-gray-700 text-gray-300 ring-gray-600'
      }`}
    >
      {active ? 'Yes' : 'No'}
    </span>
  );
}

export default function UsersPanel() {
  const [allUsers, setAllUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const firstResponse = await adminApi.post('/admin/users/list', {
        page: 1,
        pageSize: API_PAGE_SIZE,
      });

      const firstUsers = firstResponse.data.users || [];
      const nextTotalUsers = firstResponse.data.totalUsers || firstUsers.length;
      const totalPages = Math.ceil(nextTotalUsers / API_PAGE_SIZE);

      const remainingResponses = await Promise.all(
        Array.from({ length: Math.max(totalPages - 1, 0) }, (_, index) => (
          adminApi.post('/admin/users/list', {
            page: index + 2,
            pageSize: API_PAGE_SIZE,
          })
        ))
      );

      const nextUsers = [
        ...firstUsers,
        ...remainingResponses.flatMap((response) => response.data.users || []),
      ];

      setAllUsers(nextUsers);
      setTotalUsers(nextTotalUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setErrorMessage('Unable to load users.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const activeFilterOption = useMemo(
    () => userFilters.find((filter) => filter.id === activeFilter) || userFilters[0],
    [activeFilter]
  );

  const filteredUsers = useMemo(() => {
    const eligibleUsers = allUsers.filter(activeFilterOption.predicate);
    const query = appliedSearch.trim().toLowerCase();
    const baseUsers = query
      ? eligibleUsers.filter((user) => searchableValue(user).includes(query))
      : eligibleUsers;

    return [...baseUsers].sort((left, right) => {
      const leftValue = getSortValue(left, sortBy);
      const rightValue = getSortValue(right, sortBy);
      const direction = sortOrder === 'asc' ? 1 : -1;

      if (leftValue > rightValue) {
        return direction;
      }

      if (leftValue < rightValue) {
        return -direction;
      }

      return String(left.email || '').localeCompare(String(right.email || ''));
    });
  }, [activeFilterOption, allUsers, appliedSearch, sortBy, sortOrder]);

  const totalMatchingUsers = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalMatchingUsers / pageSize));
  const currentPage = Math.min(page, totalPages);
  const firstVisibleUser = totalMatchingUsers === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastVisibleUser = Math.min(currentPage * pageSize, totalMatchingUsers);
  const visibleUsers = filteredUsers.slice(firstVisibleUser === 0 ? 0 : firstVisibleUser - 1, lastVisibleUser);

  const verifiedUsers = useMemo(
    () => allUsers.filter((user) => user.isEmailVerified).length,
    [allUsers]
  );
  const premiumUsers = useMemo(
    () => allUsers.filter((user) => user.isPremiumUser).length,
    [allUsers]
  );
  const filterCounts = useMemo(() => ({
    all: totalUsers || allUsers.length,
    verified: verifiedUsers,
    premium: premiumUsers,
  }), [allUsers.length, premiumUsers, totalUsers, verifiedUsers]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedSearch(searchTerm.trim());
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setAppliedSearch('');
    setPage(1);
  };

  const handleSortByChange = (event) => {
    const nextSortBy = event.target.value;
    setSortBy(nextSortBy);
    setSortOrder(nextSortBy === 'email' ? 'asc' : 'desc');
    setPage(1);
  };

  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setPage(1);
  };

  return (
    <div className="text-base text-gray-100">
      <div className="mb-5 flex flex-col gap-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Users</h1>
            <p className="mt-1 text-base text-gray-400">
              {isLoading ? 'Loading users...' : `${totalMatchingUsers} shown`}
              {activeFilter !== 'all' ? ` in ${activeFilterOption.label}` : ''}
              {appliedSearch ? ` matching "${appliedSearch}"` : ''}
              {` from ${totalUsers || allUsers.length} total`}
            </p>
          </div>

          <form className="flex w-full flex-col gap-2 sm:flex-row xl:max-w-2xl" onSubmit={handleSearchSubmit}>
            <label className="sr-only" htmlFor="user-search">
              Search users
            </label>
            <input
              id="user-search"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search email, name, username, or ID"
              className="min-h-11 flex-1 rounded-md border border-gray-600 bg-gray-900 px-3.5 py-2.5 text-base text-gray-100 outline-none placeholder:text-gray-500 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                className="min-h-11 rounded-md bg-emerald-500 px-5 py-2.5 text-base font-medium text-gray-950 hover:bg-emerald-400"
              >
                Search
              </button>
              {appliedSearch && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="min-h-11 rounded-md border border-gray-600 px-5 py-2.5 text-base font-medium text-gray-200 hover:bg-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" role="tablist" aria-label="User eligibility filters">
          {userFilters.map((filter) => {
            const isActive = activeFilter === filter.id;

            return (
              <button
                key={filter.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleFilterChange(filter.id)}
                className={`rounded-md border px-4 py-3 text-left transition focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-gray-950 ${
                  isActive
                    ? 'border-emerald-400 bg-emerald-500/10 ring-1 ring-emerald-400/50'
                    : 'border-gray-700 bg-gray-900/50 hover:border-gray-500 hover:bg-gray-800/70'
                }`}
              >
                <div className={`text-sm font-medium ${isActive ? 'text-emerald-200' : 'text-gray-400'}`}>
                  {filter.label}
                </div>
                <div className="mt-1 text-xl font-semibold text-gray-100">
                  {filterCounts[filter.id]}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3 rounded-md border border-gray-700 bg-gray-900/40 p-3">
          <label className="flex min-w-[170px] flex-1 flex-col gap-1 text-sm font-medium text-gray-400 sm:flex-none">
            Sort
            <select
              value={sortBy}
              onChange={handleSortByChange}
              className="h-11 rounded-md border border-gray-600 bg-gray-900 px-3 text-base text-gray-100 outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex min-w-[150px] flex-1 flex-col gap-1 text-sm font-medium text-gray-400 sm:flex-none">
            Order
            <select
              value={sortOrder}
              onChange={handleSortOrderChange}
              className="h-11 rounded-md border border-gray-600 bg-gray-900 px-3 text-base text-gray-100 outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </label>

          <label className="flex min-w-[130px] flex-1 flex-col gap-1 text-sm font-medium text-gray-400 sm:flex-none">
            Rows
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="h-11 rounded-md border border-gray-600 bg-gray-900 px-3 text-base text-gray-100 outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>

          <button
            type="button"
            onClick={fetchUsers}
            disabled={isLoading}
            className="mt-auto min-h-11 rounded-md border border-gray-600 px-5 py-2.5 text-base font-medium text-gray-200 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Refresh
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-base text-red-200">
          {errorMessage}
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700 text-left text-base">
            <thead className="bg-gray-900 text-sm uppercase text-gray-400">
              <tr>
                <th className="px-4 py-3.5 font-medium">Email</th>
                <th className="px-4 py-3.5 font-medium">Name</th>
                <th className="px-4 py-3.5 font-medium">ID</th>
                <th className="px-4 py-3.5 font-medium">Verified</th>
                <th className="px-4 py-3.5 font-medium">Premium</th>
                <th className="px-4 py-3.5 font-medium">Type</th>
                <th className="px-4 py-3.5 font-medium">Signed Up</th>
                {!isStandaloneDeployment && (
                  <th className="px-4 py-3.5 text-right font-medium">Credits</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700 bg-gray-900/50">
              {isLoading && visibleUsers.length === 0 && (
                <tr>
                  <td className="px-4 py-12 text-center text-gray-400" colSpan={isStandaloneDeployment ? 7 : 8}>
                    Loading users...
                  </td>
                </tr>
              )}

              {!isLoading && visibleUsers.length === 0 && (
                <tr>
                  <td className="px-4 py-12 text-center text-gray-400" colSpan={isStandaloneDeployment ? 7 : 8}>
                    {activeFilterOption.emptyMessage}
                  </td>
                </tr>
              )}

              {visibleUsers.map((user) => (
                <tr key={user._id} className="hover:bg-gray-700/60">
                  <td className="max-w-[280px] truncate px-4 py-4 font-medium text-gray-100" title={user.email}>
                    {user.email || '-'}
                  </td>
                  <td className="max-w-[200px] truncate px-4 py-4 text-gray-300" title={user.displayName || user.username || ''}>
                    {user.displayName || user.username || '-'}
                  </td>
                  <td className="max-w-[230px] truncate px-4 py-4 font-mono text-sm text-gray-400" title={user._id}>
                    {user._id}
                  </td>
                  <td className="px-4 py-4">
                    <StatusBadge active={user.isEmailVerified} />
                  </td>
                  <td className="px-4 py-4">
                    <StatusBadge active={user.isPremiumUser} />
                  </td>
                  <td className="px-4 py-4 text-gray-300">{user.premiumUserType || 'N/A'}</td>
                  <td className="px-4 py-4 text-gray-300">{formatDate(user.createdAt)}</td>
                  {!isStandaloneDeployment && (
                    <td className="px-4 py-4 text-right font-mono text-gray-100">
                      {user.generationCredits ?? 0}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 text-base text-gray-300 sm:flex-row sm:items-center sm:justify-between">
        <span>
          Showing {firstVisibleUser}-{lastVisibleUser} of {totalMatchingUsers}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((previousPage) => Math.max(previousPage - 1, 1))}
            disabled={currentPage === 1 || isLoading}
            className="min-h-11 rounded-md border border-gray-600 px-5 py-2.5 font-medium text-gray-200 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <span className="min-w-[120px] text-center">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((previousPage) => Math.min(previousPage + 1, totalPages))}
            disabled={currentPage >= totalPages || isLoading}
            className="min-h-11 rounded-md border border-gray-600 px-5 py-2.5 font-medium text-gray-200 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
